// Settings — the creator's identity + viewer look. Edit display name,
// bio, and the About page; pick a theme + accent. "Save & republish"
// persists the profile and re-renders the whole site so the new look +
// copy go live (Sprint 6). Theme changes are a publish, exactly like the
// doc's "update theme.json + push = new look".

import { useEffect, useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { getProfile, setProfile, profileWithDefaults } from "../../lib/profile";
import { publishAll } from "../../lib/publish-flow";
import { THEMES } from "../../lib/themes";

type SaveState = "idle" | "saving" | "done" | "error";

export function SettingsTab() {
  const user = useAuthStore((s) => s.user)!;
  const [p, setP] = useState(() => profileWithDefaults(getProfile(user.sub)));
  const [state, setState] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setP(profileWithDefaults(getProfile(user.sub))); }, [user.sub]);

  const set = (patch: Partial<typeof p>) => { setP((cur) => ({ ...cur, ...patch })); setState("idle"); };

  const saveOnly = () => { setProfile(user.sub, p); setState("done"); };

  const saveAndPublish = async () => {
    setProfile(user.sub, p);
    setState("saving"); setError(null);
    try {
      await publishAll(user.sub, "BKA: update site profile + theme");
      setState("done");
    } catch (e) {
      setState("error");
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="set-root">
      <div className="set-col">
        <h1 className="set-title">Settings</h1>
        <div className="set-id">
          <span className="set-id-k">signed in as</span>
          <span className="set-id-v">{user.name} · {user.email}</span>
        </div>

        <section className="set-section">
          <h2 className="set-h2">Your site</h2>
          <label className="set-label">Display name</label>
          <input className="set-input" value={p.displayName} onChange={(e) => set({ displayName: e.target.value })} placeholder="Your name" />
          <label className="set-label">Bio <span className="set-opt">(site header + feed)</span></label>
          <input className="set-input" value={p.bio} onChange={(e) => set({ bio: e.target.value })} placeholder="One line about you" />
          <label className="set-label">About page <span className="set-opt">(markdown, optional)</span></label>
          <textarea className="set-input set-textarea" rows={5} value={p.about ?? ""} onChange={(e) => set({ about: e.target.value })} placeholder="A longer introduction for your /about page…" />
        </section>

        <section className="set-section">
          <h2 className="set-h2">Theme</h2>
          <div className="set-themes">
            {THEMES.map((t) => (
              <button key={t.id} className={"set-theme" + (p.theme === t.id ? " set-theme-on" : "")} onClick={() => set({ theme: t.id })}>
                <span className="set-theme-name">{t.label}</span>
                <span className="set-theme-vibe">{t.vibe}</span>
              </button>
            ))}
          </div>
          <label className="set-label">Accent colour</label>
          <div className="set-accent">
            <input type="color" value={p.accent} onChange={(e) => set({ accent: e.target.value })} />
            <code>{p.accent}</code>
          </div>
        </section>

        <div className="set-actions">
          <button className="set-btn" disabled={state === "saving"} onClick={() => void saveAndPublish()}>
            {state === "saving" ? "Republishing…" : "Save & republish"}
          </button>
          <button className="set-link" onClick={saveOnly}>save without publishing</button>
          {state === "done" && <span className="set-ok">saved ✓</span>}
        </div>
        {state === "error" && error && <div className="set-error">{error}</div>}
        <p className="set-note">Theme + profile changes go live when you republish — it re-renders your whole site and commits it; Cloudflare redeploys in ~30–60s.</p>
      </div>

      <style>{`
        .set-root { height: 100%; overflow: auto; display: flex; justify-content: center; padding: var(--space-6) var(--space-5); }
        .set-col { width: min(640px, 100%); }
        .set-title { font-family: var(--font-serif); font-size: 30px; font-weight: 600; margin: 0 0 var(--space-3); color: var(--ink); }
        .set-id { display: flex; gap: var(--space-3); font-size: 13px; color: var(--ink-soft); margin-bottom: var(--space-5); }
        .set-id-k { font-family: var(--font-mono); font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-faint); }
        .set-section { margin-bottom: var(--space-6); }
        .set-h2 { font-family: var(--font-serif); font-size: 18px; margin: 0 0 var(--space-3); color: var(--ink); }
        .set-label { display: block; font-size: 12px; font-weight: 600; color: var(--ink-soft); margin: var(--space-3) 0 var(--space-2); }
        .set-opt { color: var(--ink-faint); font-weight: 400; }
        .set-input { width: 100%; box-sizing: border-box; padding: 10px 12px; font-family: var(--font-sans); font-size: 14px; background: var(--paper-raised); border: 1px solid var(--line-strong); border-radius: var(--radius-1); color: var(--ink); }
        .set-input:focus { outline: none; border-color: var(--teal); }
        .set-textarea { font-family: var(--font-serif); resize: vertical; line-height: 1.6; }
        .set-themes { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--space-2); }
        .set-theme { text-align: left; padding: var(--space-3); border: 1px solid var(--line-strong); border-radius: var(--radius-1); background: var(--paper-raised); cursor: pointer; display: flex; flex-direction: column; gap: 4px; }
        .set-theme-on { border-color: var(--teal); background: var(--teal-dim); }
        .set-theme-name { font-weight: 600; font-size: 14px; color: var(--ink); }
        .set-theme-vibe { font-size: 11px; color: var(--ink-faint); line-height: 1.4; }
        .set-accent { display: flex; align-items: center; gap: var(--space-3); }
        .set-accent input[type=color] { width: 44px; height: 32px; border: 1px solid var(--line-strong); border-radius: 6px; background: none; cursor: pointer; }
        .set-accent code { font-family: var(--font-mono); font-size: 13px; color: var(--ink-soft); }
        .set-actions { display: flex; align-items: center; gap: var(--space-3); margin-top: var(--space-4); }
        .set-btn { font-family: var(--font-sans); font-size: 14px; font-weight: 600; padding: 10px 20px; border-radius: var(--radius-1); border: 1px solid var(--teal); background: var(--teal); color: #fff; cursor: pointer; }
        .set-btn:hover { background: var(--teal-bright); }
        .set-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .set-link { background: none; border: none; color: var(--ink-faint); font-size: 13px; text-decoration: underline; cursor: pointer; }
        .set-link:hover { color: var(--teal); }
        .set-ok { color: var(--teal); font-size: 13px; }
        .set-error { margin-top: var(--space-3); padding: var(--space-3); background: var(--rust-bg); border: 1px solid rgba(180,80,42,0.3); border-radius: var(--radius-1); color: var(--rust); font-size: 13px; user-select: text; }
        .set-note { font-size: 12px; color: var(--ink-faint); line-height: 1.5; margin-top: var(--space-3); }
      `}</style>
    </div>
  );
}
